import { supabase } from '../lib/supabase';
import { Usuario } from '../types/auth';

const translateAuthError = (error: string): string => {
  const translations: Record<string, string> = {
    'Invalid login credentials': 'Credenciales de inicio de sesión inválidas',
    'Email not confirmed': 'Email no confirmado',
    'User not found': 'Usuario no encontrado',
    'Invalid email or password': 'Email o contraseña inválidos',
    'Email already registered': 'El email ya está registrado',
    'Password should be at least 6 characters': 'La contraseña debe tener al menos 6 caracteres',
    'Unable to validate email address': 'No se pudo validar la dirección de email',
    'Signup requires a valid password': 'El registro requiere una contraseña válida',
    'Password is known to be weak and easy to guess, please choose a different one.': 'La contraseña es débil y fácil de adivinar, por favor elije una contraseña fuerte.',
  };

  return translations[error] || error;
};

export const authService = {
  async login(email: string, password: string) {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (error) {
      throw new Error(translateAuthError(error.message));
    }

    if (!data.user) {
      throw new Error('No se pudo obtener la información del usuario');
    }

    const userData = await this.getCurrentUser(data.user.id);
    return { authUser: data.user, usuario: userData };
  },

  async logout() {
    const { error } = await supabase.auth.signOut();
    if (error) {
      throw new Error(translateAuthError(error.message));
    }
  },

  async getCurrentUser(userId: string): Promise<Usuario | null> {
    const { data: miembroData, error: miembroError } = await supabase
      .from('miembros')
      .select('*')
      .eq('user_id', userId)
      .not('rol_id', 'is', null)
      .maybeSingle();

    if (miembroError) {
      console.error('Error fetching miembro data:', miembroError);
      throw new Error('Error al obtener datos del usuario');
    }

    if (!miembroData) {
      return null;
    }

    const { data: rolData, error: rolError } = await supabase
      .from('roles')
      .select('*')
      .eq('id', miembroData.rol_id)
      .single();

    if (rolError) {
      console.error('Error fetching rol data:', rolError);
    }

    const { data: lumisialData, error: lumisialError } = await supabase
      .from('lumisiales')
      .select(`
        *,
        diocesis:diocesis(
          id,
          nombre,
          tipo,
          provincia_eclesiastica_id,
          activo,
          created_at
        ),
        provincia_eclesiastica:provincias_eclesiasticas(
          id,
          nombre,
          codigo_iso,
          activo,
          created_at
        )
      `)
      .eq('id', miembroData.lumisial_actual_id)
      .single();

    if (lumisialError) {
      console.error('Error fetching lumisial data:', lumisialError);
    }

    const diocesisData = lumisialData?.diocesis || null;
    let provinciaData = lumisialData?.provincia_eclesiastica || null;

    if (diocesisData?.provincia_eclesiastica_id && !provinciaData) {
      const { data: provincia } = await supabase
        .from('provincias_eclesiasticas')
        .select('*')
        .eq('id', diocesisData.provincia_eclesiastica_id)
        .single();
      provinciaData = provincia;
    }

    return {
      id: miembroData.id,
      user_id: miembroData.user_id,
      rol_id: miembroData.rol_id,
      provincia_eclesiastica_id: provinciaData?.id || lumisialData?.provincia_eclesiastica_id || null,
      lumisial_id: miembroData.lumisial_actual_id,
      diocesis_id: diocesisData?.id || lumisialData?.diocesis_id || null,
      nombres: miembroData.nombres,
      apellidos: miembroData.apellidos,
      email: miembroData.email,
      telefono: miembroData.telefono,
      activo: miembroData.rol_id !== null,
      created_at: miembroData.created_at,
      updated_at: miembroData.updated_at,
      foto_url: miembroData.foto_url ?? null,
      rol: rolData,
      lumisial: lumisialData,
      diocesis: diocesisData
    };
  },

  async getSession() {
    const { data: { session }, error } = await supabase.auth.getSession();

    if (error) {
      throw new Error(translateAuthError(error.message));
    }

    return session;
  },

  async checkSession() {
    const session = await this.getSession();

    if (!session) {
      return null;
    }

    const userData = await this.getCurrentUser(session.user.id);
    return { authUser: session.user, usuario: userData };
  },

  async updatePassword(newPassword: string) {
    const { error } = await supabase.auth.updateUser({
      password: newPassword
    });

    if (error) {
      throw new Error(translateAuthError(error.message));
    }
  },

  async updateProfile(userId: string, updates: {
    nombres?: string;
    apellidos?: string;
    telefono?: string;
  }) {
    const { data, error } = await supabase
      .from('miembros')
      .update(updates)
      .eq('user_id', userId)
      .not('rol_id', 'is', null)
      .select()
      .single();

    if (error) {
      throw new Error('Error al actualizar el perfil');
    }

    return data;
  },

  async confirmUserEmail(userId: string) {
    const session = await this.getSession();
    if (!session) {
      throw new Error('No hay sesión activa');
    }

    const apiUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/confirm-user`;
    const headers = {
      'Authorization': `Bearer ${session.access_token}`,
      'Content-Type': 'application/json',
    };

    const response = await fetch(apiUrl, {
      method: 'POST',
      headers,
      body: JSON.stringify({ userId }),
    });

    const result = await response.json();

    if (!response.ok || !result.success) {
      throw new Error(result.error || 'Error al confirmar el email del usuario');
    }

    return result.data;
  }
};
